import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { WorkflowStepper } from "../components/WorkflowStepper";
import { ArrowLeft, Film, CheckCircle2, AlertCircle, ChevronRight, Lock } from "lucide-react";
import SetupStepper from "./SetupStepper";
import StoryboardView from "./StoryboardView";
import ProductionView from "./ProductionView";
import RenderStep from "./RenderStep";
import { toast } from "sonner";

const STATUS_LABELS = {
  Draft: "Draft", Generating: "Generating", StoryboardReady: "Storyboard Ready", ClipsQA: "Clips QA",
  Approved: "Approved", Rendering: "Rendering", Delivered: "Delivered", Failed: "Failed"
};

function getShots(session) {
  const sb = session?.storyboard;
  if (!sb) return [];
  return sb.shot_list || sb.shots || [];
}

export default function SessionWorkspace() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1);
  const [initialStepSet, setInitialStepSet] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${sessionId}`);
      setSession(res.data);
    } catch {
      toast.error("Session not found");
      navigate("/dashboard");
    }
    setLoading(false);
  }, [sessionId, navigate]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Poll while Generating
  useEffect(() => {
    if (!session || session.status !== 'Generating') return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/sessions/${sessionId}`);
        setSession(res.data);
        if (res.data.status !== 'Generating') {
          clearInterval(interval);
          if (res.data.generation_error) {
            toast.error(`Storyboard generation failed: ${res.data.generation_error}`);
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, sessionId]);

  // Compute step states
  const shots = useMemo(() => getShots(session), [session]);
  const hasStoryboard = shots.length > 0;
  const allClipsSelected = hasStoryboard && shots.every(s => s.selected_candidate_id);
  const production = session?.production || {};

  const compositeReady = production.agent_composite?.status === 'ready';
  const introVideoReady = production.avatar_intro?.video_status === 'ready';
  const ctaVideoReady = production.avatar_cta?.video_status === 'ready';
  const walkthroughAudioReady = (production.audio_walkthrough?.status === 'ready');
  const wowShotsReady = (production.wow_shots?.length || 0) >= 3;
  const productionComplete = compositeReady && introVideoReady && ctaVideoReady && walkthroughAudioReady && wowShotsReady;

  // Auto-set initial step based on session state (only once on load)
  useEffect(() => {
    if (!session || initialStepSet) return;
    const status = session.status;
    if (status === 'Rendering' || status === 'Delivered' || status === 'RenderFailed') {
      setActiveStep(3);
    } else if (allClipsSelected && productionComplete) {
      setActiveStep(3);
    } else if (allClipsSelected) {
      setActiveStep(2);
    }
    setInitialStepSet(true);
  }, [session, allClipsSelected, productionComplete, initialStepSet]);

  // Step 1 complete: all clips selected
  // Step 2 complete: all production assets ready
  // Step 3: render
  const step1Complete = allClipsSelected;
  const step2Complete = productionComplete;

  const stepStates = useMemo(() => {
    if (!hasStoryboard) return { 1: "current", 2: "locked", 3: "locked" };
    return {
      1: step1Complete ? "completed" : "current",
      2: step1Complete ? (step2Complete ? "completed" : "current") : "locked",
      3: step1Complete && step2Complete ? "current" : "locked",
    };
  }, [hasStoryboard, step1Complete, step2Complete]);

  const isGenerating = session?.status === "Generating";
  const isDraft = session?.status === "Draft";
  const showWorkflow = hasStoryboard || isGenerating;

  // Step requirements for footer
  const selectedCount = shots.filter(s => s.selected_candidate_id).length;

  const handleNextStep = () => {
    if (activeStep < 3) setActiveStep(activeStep + 1);
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-4 w-96 mb-8" />
        <Skeleton className="h-[500px] w-full rounded-xl" />
      </div>
    );
  }

  if (!session) return null;

  // Can we proceed to next step?
  const canAdvanceFromStep1 = step1Complete;
  const canAdvanceFromStep2 = step2Complete;

  return (
    <div className="h-full flex flex-col" data-testid="session-workspace">
      {/* Top Bar */}
      <div className="bg-white border-b border-[#E2E4E7] px-6 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="text-[#94969C] hover:text-[#020B23] transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#4D11F6]/10 flex items-center justify-center">
              <Film size={16} className="text-[#4D11F6]" />
            </div>
            <div>
              <h2 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>
                {session.property_label}
              </h2>
              <p className="text-xs text-[#94969C]">
                Updated {new Date(session.updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {showWorkflow && (
            <WorkflowStepper
              currentStep={activeStep}
              onStepChange={setActiveStep}
              stepStates={stepStates}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 overflow-auto min-h-0">
          {isDraft && !hasStoryboard && !isGenerating ? (
            <SetupStepper session={session} onUpdate={setSession} />
          ) : activeStep === 3 ? (
            <RenderStep session={session} onUpdate={setSession} allClipsSelected={allClipsSelected} productionComplete={productionComplete} shots={shots} production={production} />
          ) : activeStep === 2 ? (
            <ProductionView session={session} onUpdate={setSession} />
          ) : (
            <StoryboardView session={session} onUpdate={setSession} hideRenderButton />
          )}
        </div>

        {/* Step Footer with Continue button */}
        {showWorkflow && activeStep < 3 && (
          <StepFooter
            activeStep={activeStep}
            canAdvance={activeStep === 1 ? canAdvanceFromStep1 : canAdvanceFromStep2}
            onNext={handleNextStep}
            selectedCount={selectedCount}
            totalShots={shots.length}
            step1Complete={step1Complete}
            step2Complete={step2Complete}
            production={production}
          />
        )}
      </div>
    </div>
  );
}


// ===== STEP FOOTER =====
function StepFooter({ activeStep, canAdvance, onNext, selectedCount, totalShots, step1Complete, production }) {
  const nextLabel = activeStep === 1 ? "Continue to Production" : "Continue to Final Render";

  // Requirements text
  let reqText = "";
  let reqItems = [];
  if (activeStep === 1) {
    reqText = !step1Complete
      ? `Select a clip for each shot to continue (${selectedCount}/${totalShots} done)`
      : `All ${totalShots} clips selected`;
    reqItems = [];
  } else {
    const items = [
      { label: "Agent Composite", done: production.agent_composite?.status === 'ready' },
      { label: "Intro Video", done: production.avatar_intro?.video_status === 'ready' },
      { label: "Walkthrough VO", done: production.audio_walkthrough?.status === 'ready' },
      { label: "CTA Video", done: production.avatar_cta?.video_status === 'ready' },
      { label: "WOW Shots", done: (production.wow_shots?.length || 0) >= 3 },
    ];
    const doneCount = items.filter(i => i.done).length;
    reqText = `${doneCount}/${items.length} production assets ready`;
    reqItems = items;
  }

  return (
    <div className="bg-white border-t border-[#E2E4E7] px-6 py-3 shrink-0" data-testid="step-footer">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          {canAdvance ? (
            <CheckCircle2 size={16} className="text-emerald-500" />
          ) : (
            <AlertCircle size={16} className="text-[#94969C]" />
          )}
          <div>
            <p className={`text-sm font-medium ${canAdvance ? 'text-emerald-700' : 'text-[#676970]'}`}>
              {reqText}
            </p>
            {activeStep === 2 && reqItems.length > 0 && !canAdvance && (
              <div className="flex gap-3 mt-1">
                {reqItems.map(item => (
                  <span key={item.label} className={`text-[10px] px-2 py-0.5 rounded-full ${item.done ? 'bg-emerald-50 text-emerald-600' : 'bg-[#F3F3F9] text-[#94969C]'}`}>
                    {item.done ? '✓' : '○'} {item.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={onNext}
          disabled={!canAdvance}
          data-testid="step-continue-btn"
          className={`text-sm px-5 ${canAdvance
            ? 'bg-[#4D11F6] hover:bg-[#3A0BC0] text-white'
            : 'bg-[#E2E4E7] text-[#94969C] cursor-not-allowed'}`}
        >
          {canAdvance ? (
            <>{nextLabel} <ChevronRight size={14} className="ml-1.5" /></>
          ) : (
            <><Lock size={13} className="mr-1.5" /> {nextLabel}</>
          )}
        </Button>
      </div>
    </div>
  );
}

